// ============================================
// Seed Templates: Corporate Formation Documents
// Category: corporate-formation
// Templates: 6
// ============================================

import type { DocTemplate, QualityGate } from '../../types/docops';

const OA_QUALITY_GATE: QualityGate = {
  requiredScore: 95,
  maxAttempts: 3,
  provisions: [
    { id: 'oa-formation', title: 'Formation and Purpose', required: true, keywords: ['formation', 'purpose', 'organized', 'limited liability'], weight: 'critical' },
    { id: 'oa-members', title: 'Members and Interests', required: true, keywords: ['member', 'interest', 'capital', 'contribution'], weight: 'critical' },
    { id: 'oa-manager-managed', title: 'Manager-Managed Structure', required: true, keywords: ['manager-managed', 'managing member', 'management'], weight: 'critical' },
    { id: 'oa-ip-license', title: 'IP License Obligations', required: true, keywords: ['ip license', 'intellectual property', 'royalty', 'license'], weight: 'critical' },
    { id: 'oa-transfer', title: 'Transfer Restrictions', required: true, keywords: ['transfer', 'restriction', 'assignment', 'prohibited'], weight: 'critical' },
    { id: 'oa-change-control', title: 'Change of Control', required: true, keywords: ['change of control', 'acquisition', 'merger'], weight: 'critical' },
    { id: 'oa-distributions', title: 'Distributions', required: true, keywords: ['distribution', 'allocat', 'profit'], weight: 'high' },
    { id: 'oa-anti-dilution', title: 'Anti-Dilution', required: true, keywords: ['anti-dilution', 'dilution', 'protection'], weight: 'high' },
    { id: 'oa-dissolution', title: 'Dissolution', required: true, keywords: ['dissolution', 'winding up', 'wind up', 'dissolve'], weight: 'high' },
    { id: 'oa-arbitration', title: 'Dispute Resolution', required: true, keywords: ['arbitration', 'dispute', 'resolution', 'mediation'], weight: 'high' },
    { id: 'oa-noncompete', title: 'Non-Compete/Non-Solicit', required: true, keywords: ['non-compete', 'non-solicit', 'noncompete', 'restrictive'], weight: 'high' },
    { id: 'oa-confidentiality', title: 'Confidentiality', required: true, keywords: ['confidential', 'proprietary', 'nondisclosure'], weight: 'high' },
  ],
};

export const SEED_TEMPLATES_CORPORATE: DocTemplate[] = [
  // ── 2.1 LLC Operating Agreement ──
  {
    id: '2.1-operating-agreement',
    categoryId: 'corporate-formation',
    title: 'LLC Operating Agreement',
    description:
      'Operating agreement for the MSO LLC defining membership, management structure, IP license obligations, transfer restrictions, and dissolution provisions.',
    templateContent: `# Operating Agreement

**{{brandEntity}}**
A {{stateOfFormation}} Limited Liability Company

## Article I — Formation and Purpose
{{AI_GENERATED_CONTENT}}

## Article II — Members and Interests
{{AI_GENERATED_CONTENT}}

## Article III — Management
Managing Member / CEO: {{ceoName}}
{{AI_GENERATED_CONTENT}}

## Article IV — Capital Contributions
{{AI_GENERATED_CONTENT}}

## Article V — Distributions
{{AI_GENERATED_CONTENT}}

## Article VI — IP License Obligations
IP licensed from {{trustName}} — royalty is priority expense.
{{AI_GENERATED_CONTENT}}

## Article VII — Transfer Restrictions
{{AI_GENERATED_CONTENT}}

## Article VIII — Anti-Dilution
{{AI_GENERATED_CONTENT}}

## Article IX — Change of Control
{{AI_GENERATED_CONTENT}}

## Article X — Dissolution
{{AI_GENERATED_CONTENT}}

## Article XI — Dispute Resolution
{{AI_GENERATED_CONTENT}}

## Article XII — Non-Compete / Non-Solicit
{{AI_GENERATED_CONTENT}}

## Article XIII — Confidentiality
{{AI_GENERATED_CONTENT}}

## Article XIV — Representations and Warranties
{{AI_GENERATED_CONTENT}}

EIN: {{ein}}
Principal Address: {{businessAddress}}
`,
    requiredVariables: [
      'brandEntity',
      'stateOfFormation',
      'ceoName',
      'ein',
      'businessAddress',
      'trustName',
      'ipLicenseRoyaltyPercent',
    ],
    generationPrompt:
      'Generate Operating Agreement TERM SHEET/OUTLINE for {{brandEntity}}, a {{stateOfFormation}} LLC. Outline for attorney review, not final document. Managing Member: {{ceoName}} (CEO). IP licensed from {{trustName}} via exclusive license. Founder (IP licensor beneficiary) is NOT a member/manager. Key provisions: 1) Formation and purpose (health tech platform), 2) Members/interests/capital, 3) Management (manager-managed by CEO), 4) Distributions, 5) IP License obligations (royalty to {{trustName}} is priority expense), 6) Transfer restrictions, 7) Anti-dilution, 8) Change of control, 9) Dissolution, 10) Dispute resolution (arbitration), 11) Non-compete/non-solicit, 12) Confidentiality, 13) Reps and warranties. Format: structured outline with bracketed terms for attorney, include notes explaining business rationale.',
    attorneyReviewRequired: true,
    qualityGate: OA_QUALITY_GATE,
    launchBlocker: true,
    estimatedPages: 20,
    sortOrder: 1,
    generationRound: 1,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: ['ip-licensed-model'],
  },

  // ── 2.2 Board Resolution: CEO Appointment ──
  {
    id: '2.2-resolution-ceo-appointment',
    categoryId: 'corporate-formation',
    title: 'Board Resolution: CEO Appointment',
    description:
      'Formal resolution appointing the CEO with authority to sign contracts, manage operations, hire/fire, and manage bank accounts.',
    templateContent: `# Board Resolution — CEO Appointment

**{{brandEntity}}**

Effective Date: {{effectiveDate}}

WHEREAS, {{brandEntity}} requires executive leadership; and

WHEREAS, the Members desire to appoint {{ceoName}} as Chief Executive Officer;

NOW, THEREFORE, BE IT RESOLVED:

{{AI_GENERATED_CONTENT}}

_________________________
Authorized Member Signature
`,
    requiredVariables: ['brandEntity', 'ceoName', 'effectiveDate'],
    generationPrompt: `Generate a formal Board Resolution for {{brandEntity}} appointing {{ceoName}} as Chief Executive Officer effective {{effectiveDate}}.

RESOLVED CLAUSES — each must be a separate "RESOLVED" paragraph:
1. {{ceoName}} is hereby appointed Chief Executive Officer of {{brandEntity}}
2. CEO is authorized to execute contracts and agreements on behalf of {{brandEntity}}
3. CEO is authorized to manage day-to-day operations of the company
4. CEO is authorized to hire, terminate, and manage all personnel
5. CEO is authorized to open and manage bank accounts in the name of {{brandEntity}}
6. CEO is authorized to engage vendors and service providers as needed
7. All prior actions taken by {{ceoName}} on behalf of {{brandEntity}} are hereby ratified and approved

FORMAT RULES:
- Use formal WHEREAS/RESOLVED legal structure throughout
- Use actual entity and person names ({{brandEntity}}, {{ceoName}}) — NEVER bracket placeholders like [X] or [Insert]
- Do NOT include meta-commentary, notes, or instructions in the output
- Do NOT use {{curly}} placeholders for any value not listed above
- End with signature block and date line`,
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 2,
    sortOrder: 2,
    generationRound: 2,
    dependencies: ['2.1-operating-agreement'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 2.3 Board Resolution: Bank Account ──
  {
    id: '2.3-resolution-bank-account',
    categoryId: 'corporate-formation',
    title: 'Board Resolution: Bank Account',
    description:
      'Formal resolution authorizing the opening of a business bank account with designated signatories.',
    templateContent: `# Board Resolution — Bank Account

**{{brandEntity}}**

Effective Date: {{effectiveDate}}

WHEREAS, {{brandEntity}} requires a business bank account for operations; and

WHEREAS, {{ceoName}} has been appointed as CEO;

NOW, THEREFORE, BE IT RESOLVED:

{{AI_GENERATED_CONTENT}}

_________________________
Authorized Member Signature
`,
    requiredVariables: ['brandEntity', 'ceoName', 'effectiveDate'],
    generationPrompt: `Generate a formal Board Resolution for {{brandEntity}} authorizing the opening of a business bank account effective {{effectiveDate}}.

RESOLVED CLAUSES — each must be a separate "RESOLVED" paragraph:
1. {{brandEntity}} is authorized to open a business checking and savings account at a federally insured financial institution
2. {{ceoName}} (CEO) is designated as the primary authorized signatory on all accounts
3. {{ceoName}} is authorized to make deposits, withdrawals, wire transfers, and ACH transactions
4. {{ceoName}} is authorized to execute any banking documents required to open and maintain accounts
5. Any single signatory may authorize transactions up to a specified threshold; transactions above that threshold require two authorized signatories
6. The company treasurer or designee shall maintain records of all account activity

FORMAT RULES:
- Use formal WHEREAS/RESOLVED legal structure throughout
- Use actual entity and person names ({{brandEntity}}, {{ceoName}}) — NEVER bracket placeholders like [X] or [Insert]
- Do NOT include meta-commentary, notes, or instructions in the output
- Do NOT use {{curly}} placeholders for any value not listed above
- End with signature block and date line`,
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 2,
    sortOrder: 3,
    generationRound: 2,
    dependencies: ['2.1-operating-agreement'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 2.4 Board Resolution: Vendor Contracts ──
  {
    id: '2.4-resolution-vendor-contracts',
    categoryId: 'corporate-formation',
    title: 'Board Resolution: Vendor Contracts',
    description:
      'Formal resolution authorizing the CEO to enter into vendor contracts including healthcare vendor agreements, BAAs, and pharmacy partnerships.',
    templateContent: `# Board Resolution — Vendor Contracts

**{{brandEntity}}**

Effective Date: {{effectiveDate}}

WHEREAS, {{brandEntity}} requires vendor partnerships for platform operations; and

WHEREAS, {{ceoName}} (CEO) is authorized to act on behalf of the LLC;

NOW, THEREFORE, BE IT RESOLVED:

{{AI_GENERATED_CONTENT}}

_________________________
Authorized Member Signature
`,
    requiredVariables: ['brandEntity', 'ceoName', 'effectiveDate'],
    generationPrompt: `Generate a formal Board Resolution for {{brandEntity}} authorizing {{ceoName}} (CEO) to enter into vendor contracts effective {{effectiveDate}}.

RESOLVED CLAUSES — each must be a separate "RESOLVED" paragraph:
1. {{ceoName}} (CEO) is authorized to negotiate, execute, and manage vendor agreements on behalf of {{brandEntity}}
2. Authorized vendor categories include: healthcare service providers, telehealth networks, pharmacy partners, technology vendors, cloud infrastructure providers, payment processors, and professional service firms
3. {{ceoName}} is authorized to execute Business Associate Agreements (BAAs) as required under HIPAA for any vendor receiving protected health information
4. {{ceoName}} is authorized to execute data processing agreements with technology vendors
5. Vendor agreements with annual value exceeding a material threshold shall be reported to the Members at the next regular meeting
6. All vendor agreements shall include standard indemnification, insurance, and termination provisions

FORMAT RULES:
- Use formal WHEREAS/RESOLVED legal structure throughout
- Use actual entity and person names ({{brandEntity}}, {{ceoName}}) — NEVER bracket placeholders like [X] or [Insert]
- Do NOT include meta-commentary, notes, or instructions in the output
- Do NOT use {{curly}} placeholders for any value not listed above
- End with signature block and date line`,
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 2,
    sortOrder: 4,
    generationRound: 2,
    dependencies: ['2.1-operating-agreement'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 2.5 EIN Confirmation Letter ──
  {
    id: '2.5-ein-confirmation',
    categoryId: 'corporate-formation',
    track: 'reference',
    title: 'EIN Confirmation Letter',
    description:
      'Reference document — the IRS EIN confirmation letter (CP 575). Upload the actual letter received from the IRS.',
    templateContent: `# EIN Confirmation Letter (CP 575)

**{{brandEntity}}**
EIN: {{ein}}
Address: {{businessAddress}}

_This is a reference document. Upload the actual IRS CP 575 letter._
`,
    requiredVariables: ['brandEntity', 'ein', 'businessAddress'],
    generationPrompt:
      'This is a reference document — the IRS EIN confirmation letter (CP 575) for {{brandEntity}} (EIN: {{ein}}). No generation needed; upload the actual IRS letter.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 1,
    sortOrder: 5,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 2.6 Articles of Organization ──
  {
    id: '2.6-articles-of-organization',
    categoryId: 'corporate-formation',
    track: 'reference',
    title: 'Articles of Organization',
    description:
      'Reference document — the filed Articles of Organization from the state. Upload the actual filed document.',
    templateContent: `# Articles of Organization

**{{brandEntity}}**
State: {{stateOfFormation}}
Principal Address: {{businessAddress}}
Registered Agent Address: {{registeredAgentAddress}}

_This is a reference document. Upload the actual filed Articles of Organization from {{stateOfFormation}}._
`,
    requiredVariables: [
      'brandEntity',
      'stateOfFormation',
      'businessAddress',
      'registeredAgentAddress',
    ],
    generationPrompt:
      'This is a reference document — the filed Articles of Organization for {{brandEntity}} in {{stateOfFormation}}. No generation needed; upload the filed document from the state.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 2,
    sortOrder: 6,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },
];
